import { TeamLineup, MatchConfig } from './utils/data';
import { useStringsContext } from '../../../contexts/strings';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import PlayerAvatar from '../../common/player-avatar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SxStyle } from '../../../types';
import ListEditButton from '../../common/list-edit-button';
import { getMatchesConfigPath } from '../../../utils/paths';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import firebaseApp from '../../../firebase-app';
import { doc } from 'firebase/firestore';
import { Chip } from '@mui/material';

const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 52;

const styles: SxStyle = {
  header: {
    margin: '16px 16px 0px',
  },
  dataGrid: (theme) => ({
    border: 0,
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
};

const ListPlayerConfig = ({
  playerId,
  matchConfig,
  onEditPlayer,
}: {
  playerId: string;
  matchConfig: MatchConfig;
  onEditPlayer: (playerId: string) => void;
}) => {
  const { strings } = useStringsContext();
  const { hidePlayerStats } = matchConfig || {};

  const { maxSpeed, distance } = hidePlayerStats?.[playerId] || {};

  const statsHidden = maxSpeed || distance;

  return (
    <div>
      {statsHidden && (
        <Chip color="warning" label={strings.matches.playerStatsHidden} size="small" />
      )}
    </div>
  );
};

export default function MatchLineup({
  leagueAbbrv,
  season,
  matchId,
  teamLineup,
  onEditPlayer,
}: {
  leagueAbbrv: string;
  season: string;
  matchId: string;
  teamLineup: TeamLineup;
  onEditPlayer: (playerId: string) => void;
}) {
  const { strings } = useStringsContext();

  const matchesConfigPath = getMatchesConfigPath(leagueAbbrv, season);

  const [matchConfig] = useDocumentData(doc(firebaseApp.db, `${matchesConfigPath}/${matchId}`));

  const columns: GridColDef[] = [
    {
      field: 'id',
    },
    {
      field: 'number',
      headerName: strings.squadPlayer.shirtNumber,
      align: 'right',
    },
    {
      field: 'imageHalf200',
      headerName: strings.squad.playerImage,
      renderCell: (params: GridRenderCellParams) => (
        <PlayerAvatar srcImage={params.value} fullName={params.row.givenName} />
      ),
      align: 'center',
    },
    {
      field: 'givenName',
      headerName: strings.squadPlayer.givenName,
      minWidth: 200,
    },
    {
      field: 'surName',
      headerName: strings.squadPlayer.surName,
      minWidth: 200,
    },
    {
      field: 'position',
      headerName: strings.squadPlayer.position,
      valueGetter: (value) => (value ? strings.squadPlayerPositions[value] : undefined),
    },
    {
      field: 'fogisId',
      headerName: strings.squadPlayer.fogisId,
      align: 'right',
    },
    {
      field: 'stats',
      headerName: 'Statistik',
      align: 'center',
      sortable: false,

      renderCell: (params: GridRenderCellParams) => (
        <ListPlayerConfig
          playerId={params.row.id}
          matchConfig={matchConfig as MatchConfig}
          onEditPlayer={onEditPlayer}
        />
      ),
    },
    {
      field: 'edit',
      headerName: '',
      align: 'right',
      maxWidth: 60,
      renderCell: (params: GridRenderCellParams) => (
        <ListEditButton onEdit={() => onEditPlayer(params.row.id)} />
      ),
    },
  ];

  if (!teamLineup) {
    return (
      <Typography variant="body1" sx={{ padding: '16px', textAlign: 'center' }}>
        {strings.matches.lineupMissing}
      </Typography>
    );
  }

  return (
    <>
      <Stack sx={styles.header} spacing={4} direction="row" alignItems="center">
        <Typography variant="h5">Startelva</Typography>
        <Typography variant="h6">{teamLineup.formation}</Typography>
      </Stack>

      <DataGrid
        sx={styles.dataGrid}
        style={{
          height: TABLE_HEADER_HEIGHT + TABLE_ROW_HEIGHT * teamLineup.starting.length,
        }}
        rows={teamLineup.starting}
        columns={columns}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        hideFooter
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
          sorting: {
            sortModel: [
              {
                field: 'number',
                sort: 'asc',
              },
            ],
          },
        }}
      />
      <Typography variant="h5" sx={{ m: '16px 16px 0px' }}>
        Avbytare
      </Typography>
      <DataGrid
        sx={styles.dataGrid}
        style={{
          height: TABLE_HEADER_HEIGHT + TABLE_ROW_HEIGHT * teamLineup.substitutes.length,
        }}
        rows={teamLineup.substitutes}
        columns={columns}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        hideFooter
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
          sorting: {
            sortModel: [
              {
                field: 'number',
                sort: 'asc',
              },
            ],
          },
        }}
      />
    </>
  );
}
